import { CalendarSettingsSchema, HolidaySchema, paginatedResponseSchema, PermissionTypeSchema } from '@gestion-permisos/contracts';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../api/api-context.js';

const PermissionTypePageSchema = paginatedResponseSchema(PermissionTypeSchema);
const HolidayListSchema = z.array(HolidaySchema);

/** Tipos activos: el catálogo completo en una página (la API admite hasta 100 por página). */
export function usePermissionTypes() {
  const api = useApi();
  return useQuery({
    queryKey: ['catalog', 'permission-types', 'active'],
    queryFn: () => api.get('/api/v1/permission-types?active=true&pageSize=100', PermissionTypePageSchema),
  });
}

/** Jornada y tramos vigentes (zona de negocio). */
export function useCalendarSettings() {
  const api = useApi();
  return useQuery({
    queryKey: ['catalog', 'calendar-settings'],
    queryFn: () => api.get('/api/v1/calendar/settings', CalendarSettingsSchema),
  });
}

/**
 * Festivos de un año, solo como ayuda en el formulario: no impiden guardar ni enviar (lo decide el 422 del
 * servidor). El año sale del texto de la fecha (`YYYY-…`), sin convertirla a `Date`.
 */
export function useHolidays(permissionDate: string) {
  const api = useApi();
  const year = /^(\d{4})-\d{2}-\d{2}$/.exec(permissionDate)?.[1];
  const valid = year !== undefined && Number(year) >= 2000 && Number(year) <= 2100;
  return useQuery({
    queryKey: ['catalog', 'holidays', year],
    queryFn: () => api.get(`/api/v1/calendar/holidays?year=${year}`, HolidayListSchema),
    enabled: valid,
  });
}

/**
 * Tramos `HH:mm` entre el inicio y el fin de la jornada, cada `slotMinutes`, **como texto**: se cuenta en
 * minutos enteros, sin `Date` ni zona horaria. No valida nada: el servidor decide si un horario es válido.
 */
export function timeSlots(start: string, end: string, slotMinutes: number): string[] {
  const toMinutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
  const toText = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  const slots: string[] = [];
  if (slotMinutes <= 0) return slots;
  for (let minutes = toMinutes(start); minutes <= toMinutes(end); minutes += slotMinutes) slots.push(toText(minutes));
  return slots;
}
